export class ProductCartItem {
  private productCartItems: NodeListOf<HTMLElement>;

  private isUpdating: boolean = false;

  constructor(private document: Document) {
    this.productCartItems = this.document.querySelectorAll('.product-cart__item');
  }

  public initialize(): void {
    this.productCartItems.forEach((item) => {
      item.addEventListener('click', this.handleProductCartClick.bind(this));
    });
  }

  private async handleProductCartClick(event: Event): Promise<void> {
    if (this.isElement(event.target) && event.target.closest('.product-cart__item-price-reference-description a')) {
      return;
    }

    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    event.preventDefault();
    event.stopPropagation();

    const target = event.currentTarget as HTMLElement;

    const expirationDate: string | undefined = target.dataset.expirationDate;
    const variantId: string | undefined = target.dataset.variantId;
    const donationMoney: string | undefined = target.dataset.donationMoney;
    const productTitle: string | undefined = target.dataset.productTitle;

    try {
      this.updateFavoriteButton(variantId);
      this.updateProductTitle(productTitle);
      this.updateProductForms(expirationDate);
      this.updateProductVariants(variantId);
      this.updateDonationMoney(donationMoney);
      this.toggleSelectedClass(variantId);

      await this.updateSections(variantId);
    } catch (error) {
      console.error('Error in handleProductCartClick:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  private isElement(target: EventTarget | null): target is Element {
    return target instanceof Element;
  }

  private updateFavoriteButton(variantId: string | undefined): void {
    const selectedItems = Array.from(this.document.getElementsByClassName('product-pricing__wishlist selected'));
    selectedItems.forEach((item: HTMLElement) => {
      item.classList.remove('selected');
    });

    const newSelectedItems = Array.from(
      this.document.getElementsByClassName('product-pricing__wishlist v' + variantId),
    );
    newSelectedItems.forEach((item: HTMLElement) => {
      item.classList.add('selected');
    });
  }

  private updateProductTitle(productTitle: string | undefined): void {
    this.updateInnerHTMLByClassName('product-cart__title', productTitle);
  }

  private updateProductForms(expirationDate: string | undefined): void {
    const productForms = this.document.querySelectorAll('.product-form form');
    productForms.forEach((productForm: HTMLFormElement) => {
      productForm.dataset.expirationDate = expirationDate;
    });
  }

  private updateProductVariants(variantId: string | undefined): void {
    const productVariantInputs = this.document.getElementsByName('id');
    this.updateElementsValue(productVariantInputs, variantId);
  }

  private updateDonationMoney(donationMoney: string | undefined): void {
    const donationMoneyInputs = this.document.getElementsByName('money_donation_per_product');
    this.updateElementsValue(donationMoneyInputs, donationMoney);
    this.updateInnerHTMLByClassName('product-cart__support-amount', `${donationMoney}円`);
  }

  private toggleSelectedClass(variantId: string | undefined): void {
    const selectedItems = Array.from(this.document.getElementsByClassName('product-cart__item selected'));
    selectedItems.forEach((item: HTMLElement) => {
      item.classList.remove('selected');
    });

    const newSelectedItems = Array.from(this.document.getElementsByClassName('product-cart__item v' + variantId));
    newSelectedItems.forEach((item: HTMLElement) => {
      item.classList.add('selected');
    });
  }

  private updateElementsValue(elements: NodeListOf<Element>, value: string | undefined): void {
    elements.forEach((element: HTMLInputElement) => {
      element.value = value || '';
    });
  }

  private updateInnerHTMLByClassName(className: string, innerHTML: string | undefined): void {
    const elements = Array.from(this.document.getElementsByClassName(className));
    elements.forEach((element: HTMLElement) => {
      element.innerHTML = innerHTML || '';
    });
  }

  private async updateSections(variantId: string | undefined): Promise<void> {
    const sections = [
      { selector: '.product-variant-warning__section', id: 'product-variant-warning' },
      { selector: '.product-variant-info-detail__section', id: 'product-variant-info-detail' },
      { selector: '.product-variant-sp-product-info-footer__section', id: 'product-variant-sp-product-info-footer' },
      { selector: '.product-variant-cart__section', id: 'product-variant-cart' },
      { selector: '.product-variant-sp-product-info__section', id: 'product-variant-sp-product-info' },
      {
        selector: '.product-variant-image-slider__section',
        id: 'product-variant-image-slider',
        callback: () => this.initializeSlickSlider(),
      },
      { selector: '.product-variant-listing-reason__section', id: 'product-variant-listing_reason' },
    ];

    const updatePromises = sections.map((section) =>
      this.updateSection(section.selector, section.id, variantId, section.callback),
    );

    await Promise.all(updatePromises);
  }

  private async updateSection(
    selector: string,
    section_id: string,
    variantId: string | undefined,
    callback?: () => void,
  ): Promise<void> {
    try {
      const response = await fetch(`${window.location.pathname}?variant=${variantId}&section_id=${section_id}`);
      const html = await response.text();
      this.document.querySelectorAll(selector).forEach((element) => {
        element.innerHTML = html;

        const newProductCartItems = element.querySelectorAll('.product-cart__item');
        newProductCartItems.forEach((item: HTMLElement) => {
          item.addEventListener('click', this.handleProductCartClick.bind(this));
        });
      });

      if (callback) {
        callback();
      }
    } catch (error) {
      console.error(`Error updating section ${section_id}:`, error);
    }
  }

  private initializeSlickSlider(retryCount: number = 0, maxRetries: number = 10): void {
    const sliderElement = $('.product__slider__main');
    const thumbsElement = $('.product__slider__thumbs');

    if (sliderElement.length > 0) {
      if (sliderElement.hasClass('slick-initialized')) {
        return;
      }

      const hasThumbSlider = thumbsElement.length > 0;

      (sliderElement as any).slick({
        lazyLoad: 'ondemand',
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: !hasThumbSlider,
        fade: true,
        prevArrow: '<p class="slick-prev"></p>',
        nextArrow: '<p class="slick-next"></p>',
        asNavFor: hasThumbSlider ? '.product__slider__thumbs' : undefined,
      });

      if (hasThumbSlider && !thumbsElement.hasClass('slick-initialized')) {
        (thumbsElement as any).slick({
          slidesToShow: 5,
          slidesToScroll: 1,
          asNavFor: '.product__slider__main',
          focusOnSelect: true,
          arrows: true,
          prevArrow: '<p class="slick-prev"></p>',
          nextArrow: '<p class="slick-next"></p>',
          infinite: true,
          responsive: [
            {
              breakpoint: 750,
              settings: {
                slidesToShow: 4,
              },
            },
          ],
        });
      }

      setTimeout(() => {
        this.initializeSlickSlider(retryCount + 1, maxRetries);
      }, 200);
    } else if (retryCount < maxRetries) {
      setTimeout(() => {
        this.initializeSlickSlider(retryCount + 1, maxRetries);
      }, 200);
    } else {
      console.error('Failed to initialize Slick slider: Element not found after maximum retries');
    }
  }
}
